<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    data-js-sign-post
  >
    
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="
          !product.hasOnlyDefaultVariant ||
            section.displaySingleValueOptions && section.displaySingleVariantOption
        "
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        :country ="country"
        :location="location"
        @update-variant="handleUpdateVariant"
      />

      <product-attachment-options
        v-if="product.attachments && product.attachments.length > 0 || product.bundles  && product.bundles.length > 0 "
        :attachments="product.attachments"
        :bundles="product.bundles"
        :giftui= giftui 
        @update-selected-attachments="handleUpdateSelectedAttachments"
      />

      <quantity-selector
        v-if="section.displayQuantitySelector"
        class="product-form__quantity-selector"
        large
        :min="1"
        :quantity="quantity"
        :state="loading ? 'loading' : 'ready'"
        @update-quantity="handleUpdateQuantity"
      />

      <input
        v-else
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <btn v-if="product.type != 'sawmill'"
        class="product-form__add-to-cart"
        :class="{'product-form__add-to-cart--mobile-no-price': !mobileAtcHasPrice}"
        :label-html="addToCartText"
        :show-icon="false"
        type="submit"
      />
    </form>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />
  </div>
  
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ProductPrices from '~global/product-prices/product-prices'
import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import ProductOptions from '~async/product-form/product-options'
import ProductAttachmentOptions from '~async/product-form/product-attachment-options'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import { formatNoDecimal } from '~/helpers/format'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    NotificationPanel,
    ProductPrices,
    ProductOptions,
    ProductAttachmentOptions,
    QuantitySelector,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    liquid: {
      type: Object,
      default: () => ({}),
    },
    section: {
      type: Object,
      default: () => ({
        displaySingleVariantOption: false,
        displayQuantitySelector: false,
        resetFormBehaviour: 'disabled',
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        initialSelect: true,
        selectDisabledValues: true,
      }),
    },
    country :{
      type: String ,
      default:''
    },
    location :{
      type: String ,
      default:''
    },
    variant: {
      type:  Object,
      required: true,
    },
    giftui: {
      type: [Array],
      default: () => {
        return []
      },
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'update-attachment-subtotal': () => true,
  },

  data() {
    return {
      error: '',
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      timeout: {},
      selectedAttachments: [],
      selectedAttachmentsPriceSubtotal: 0,
      compatibilList: null,
      compatibilListSingle: null,
      currentVariantId: new URLSearchParams(window.location.search).get('variant') || 0,
      newprice : '' ,
      currentCountryName: cnvs.store.countryName,
      productCountry : cnvs.store.country
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute mobile subtotal.
     * @returns {Number}
     */
    mobileSubtotal() {
      return this.variant.price?.amount + this.selectedAttachmentsPriceSubtotal
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      let text = ''
      let price = ''
      if (this.variant.available) {
        text = this.state === 'added'
          ? this.$string('product.cta.added_to_cart')
          : this.$string('product.cta.add_to_cart')
      } else {
        text = this.$string('product.cta.sold_out')

      }

      text = `<span>${text}</span>`

      if (this.isMobile && this.variant) {
        price = `<span class="main-product__mobile-atc-text">
          ${formatNoDecimal(this.$formatMoney(this.mobileSubtotal))}
        </span><span class="mx-2">|</span>`
      }

      return `${price}${text}`
    },

    mobileAtcHasPrice() {
      return this.isMobile && this.variant
    },
    currencySymbol() {
      return cnvs.store.currencySymbol
    },
  },

  watch: {

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1
      this.error = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
    },
  },

  mounted(){
    this.newprice = this.variant.price?.amount + this.selectedAttachmentsPriceSubtotal

  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),
    getVariantComptaibleValue() {
      // const id = new URLSearchParams(window.location.search).get('variant');
     
      // if (!id || !this.product || !this.product.variants) {
      //   return;
      // }

      // let shippingAvailable = null;
      // let  shippingAvailableSingle = null
      // for (let val of this.product.variants) {
      //   if ( val['id'] == id) {
      //     if(val['compatibleWith'] != null){
      //         if( val['compatibleWith'].includes(",")){
      //           shippingAvailable = val['compatibleWith'].split(',') 
      //         }else if( val['compatibleWith'].includes("|")) {
      //           shippingAvailable =  val['compatibleWith'].split('|')
      //         }else{
      //           shippingAvailableSingle =  val['compatibleWith']
      //         }

      //     }else{
      //       shippingAvailable = null
      //       shippingAvailableSingle = null
      //     }
      //     break;
      //   }
      // }
      // this.compatibilList = shippingAvailable;
      // this.compatibilListSingle = shippingAvailableSingle
     
    },
    formatNoDecimal(string) {
      return string.replace('.00', '')
    },
    /**
     * Handle update selected attachments.
     * @param {Array} selectedAttachment - Selected attachments.
     */
    handleUpdateSelectedAttachments(selectedAttachment) {
      this.selectedAttachments = selectedAttachment
      this.selectedAttachmentsPriceSubtotal = this.selectedAttachments.map((attachment) => {
        return attachment.price.amount
      }).reduce((a, b) => a + b, 0)

      this.$emit('update-attachment-subtotal', this.selectedAttachmentsPriceSubtotal)
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
         
      if (!this.variant) {
        this.error = this.$string('product.form.select_variant')
        return
      }
      let locations = this.location ? this.location.split(',') : []; // Ensure it's an array
      let countries = this.country ? this.country.split(',') : []; // Ensure it's an array
      //console.log('Checking location:', locations);
      if (locations.includes(String(this.variant.id)) && countries.includes(this.productCountry.toLowerCase())) {
          //console.log('✅ Location & country match');
          //console.log('❌ Location value is false, returning false');
          return false; 
      }
         
      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      if (this.product.compatibility) {
        this.lineItemProperties['Works with'] = this.product.compatibility
      }

      if (this.variant.leadTime) {
        this.lineItemProperties['Ships in'] = this.variant.leadTime
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      if (this.selectedAttachments.length > 0) {
        const attachmentItems = this.selectedAttachments.map((attachment) => {
          const lineItemProperties = {}
          if (attachment.compatibility) {
            lineItemProperties['Works with'] = attachment.compatibility
          }

          if (attachment.leadTime) {
            lineItemProperties['Ships in'] = attachment.leadTime
          }

          return {
            id: attachment.id,
            quantity: 1,
            properties: lineItemProperties,
          }
        })

        items.push(...attachmentItems)
      }

      try {
        await this.addItem(items)
        this.state = 'added'

        if (
          cnvs.settings.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'

      /**
       * Clear variant selection based on setting.
       */
      if (this.section.resetFormBehaviour !== 'clear') {
        return
      }

      this.handleUpdateVariant(false)
      this.quantity = 1
    },

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
      if(this.product.variants.length > 1  ){
        if( variant.compatibleWith && variant.compatibleWith.includes(',')){
          this.compatibilList = variant.compatibleWith != null ? variant.compatibleWith.split(',') : null
          this.compatibilListSingle= null
        }else if( variant.compatibleWith && variant.compatibleWith.includes('|')){
          this.compatibilList = variant.compatibleWith != null ? variant.compatibleWith.split('|') : null
          this.compatibilListSingle= null
        }else{
          this.compatibilListSingle = variant.compatibleWith
          this.compatibilList = null
        }
      }
      if(this.product.variants.length === 1 ){
        if( this.product.compatibility && this.product.compatibility.includes(',')){
          this.compatibilList = this.product.compatibility != null ? this.product.compatibility.split(',') : null
          this.compatibilListSingle= null
        }else if( this.product.compatibility && this.product.compatibility.includes('|')){
          this.compatibilList = this.product.compatibility != null ? this.product.compatibility.split('|') : null
          this.compatibilListSingle= null
        }else{
          this.compatibilListSingle = this.product.compatibility
          this.compatibilList = null
        }
      }
    },

    /**
     * Handle update quantity event.
     * @param {String} quantity - Emitted quantity.
     */
    handleUpdateQuantity(quantity) {
      this.quantity = quantity
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
@import './product-attachment-options';
</style>
